import '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { QUERY } from '../gql-operations';
import { GET_FEATURE_FLAG } from '../gql-operations/featureFlags';
import { EXTRACT_URL_TRANSCRIPT } from '../gql-operations/mutations/message/extract-url-transcript';
import { TRANSCRIBE_AUDIO } from '../gql-operations/mutations/message/transcribe-audio';
import { PRIMARY_COLOR, PRIMARY_COLOR_1, PRIMARY_COLOR_4 } from '../appearance/Colors';
import Modal from './Modals/Modal';
import customAnalytics from '../util/custom-analytics';
import {
    evaluateFeatureFlagForUser,
    mapTranscriptionErrorToUiAction
} from '../util/featureFlagAccess';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../util/featureUpgradeModalEvents';
import {
    getAudioProcessingStatusText,
    isAudioProcessingActive
} from '../util/audioProcessingStatus';

const URL_TRANSCRIPT_FLAG = 'url_transcript';
const VIDEO_TRANSCRIPTION_ACCESS_FLAG = 'social_video_auto_transcription';
const URL_TRANSCRIPT_INTRO_DISMISS_KEY = 'luckynote_hide_url_transcript_intro';
const URL_TRANSCRIPT_STALE_PROCESSING_MS = 10 * 60 * 1000;
const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be'
]);
const INSTAGRAM_HOSTS = new Set([
    'instagram.com',
    'www.instagram.com',
    'instagr.am',
    'www.instagr.am'
]);
const TIKTOK_HOSTS = new Set([
    'tiktok.com',
    'www.tiktok.com',
    'm.tiktok.com',
    'vt.tiktok.com'
]);
const X_VIDEO_HOSTS = new Set([
    'x.com',
    'www.x.com',
    'mobile.x.com',
    'twitter.com',
    'www.twitter.com',
    'mobile.twitter.com',
    'video.twimg.com'
]);

const parseUrl = (urlValue) => {
    if (!urlValue || typeof urlValue !== 'string') return null;
    const trimmed = urlValue.trim();
    if (!trimmed) return null;

    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        return new URL(candidate);
    } catch {
        return null;
    }
};

const resolvePrimaryUrl = ({ metaData, message, urlMetaIndex }) => {
    const candidates = [
        metaData?.url,
        message?.urlMeta?.[urlMetaIndex]?.url
    ];

    for (const candidate of candidates) {
        const parsed = parseUrl(candidate);
        if (!parsed) continue;
        return parsed.toString();
    }

    return null;
};

const resolveYouTubeUrl = (primaryUrl) => {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;
    return YOUTUBE_HOSTS.has(parsed.hostname.toLowerCase()) ? parsed.toString() : null;
};

const hasTranscribableVideoMeta = (metaData) => {
    if (!metaData) {
        return false;
    }
    if ((metaData.media_url || metaData.media_cdn_url) && metaData.media_has_audio !== false) {
        return true;
    }
    const gallery = Array.isArray(metaData.gallery) ? metaData.gallery : [];
    return gallery.some(slide => (
        String(slide?.type || '').toLowerCase() === 'video' &&
        (slide?.media_url || slide?.media_cdn_url || slide?.media_hls_url || slide?.media?.url || slide?.media?.cdn_url) &&
        slide?.media_has_audio !== false
    ));
};

const resolveSocialVideoUrl = (primaryUrl, metaData) => {
    const parsed = parseUrl(primaryUrl);
    if (!parsed) return null;

    const host = parsed.hostname.toLowerCase();
    if (INSTAGRAM_HOSTS.has(host)) {
        if (String(metaData?.media_type || '').toLowerCase() === 'carousel' && !hasTranscribableVideoMeta(metaData)) {
            return null;
        }
        return parsed.toString();
    }

    if (TIKTOK_HOSTS.has(host)) {
        const path = parsed.pathname.toLowerCase();
        if (path.includes('/photo/') || path.includes('/@photo/')) {
            return null;
        }
        return parsed.toString();
    }

    return null;
};

const resolveXVideoUrl = (primaryUrl, metaData) => {
    const parsed = parseUrl(primaryUrl || metaData?.media_url || metaData?.media_cdn_url);
    if (!parsed) return null;

    const host = parsed.hostname.toLowerCase();
    if (!X_VIDEO_HOSTS.has(host)) {
        return null;
    }

    const hasVideoMeta = Boolean(
        hasTranscribableVideoMeta(metaData) ||
        String(metaData?.media_type || '').toLowerCase() === 'video'
    );

    return hasVideoMeta || host === 'video.twimg.com' ? parsed.toString() : null;
};

const sanitizeClientFacingLinkToolError = (rawMessage, fallbackMessage) => {
    const normalizedRaw = String(rawMessage || '').replace(/\r/g, '\n').trim();
    if (!normalizedRaw) {
        return fallbackMessage;
    }

    const firstLine = normalizedRaw
        .split('\n')
        .map((line) => line.trim())
        .find(Boolean) || '';
    const lower = `${normalizedRaw}\n${firstLine}`.toLowerCase();

    if (
        /\bhttp(?:\s+error|\s*:\s*)?\s*429\b/.test(lower) ||
        lower.includes('too many requests') ||
        lower.includes('rate limit exceeded') ||
        lower.includes('rate-limit reached')
    ) {
        return 'Transcript service is temporarily busy. Please try again in a moment.';
    }

    if (
        /^traceback/i.test(firstLine) ||
        lower.includes('traceback (most recent call last)') ||
        /yt-?dlp/i.test(lower) ||
        lower.includes('spawnargs=') ||
        lower.includes('site-packages')
    ) {
        return fallbackMessage;
    }

    return firstLine.slice(0, 240);
};

const resolveActionErrorMessage = (error, fallbackMessage) => {
    const graphQlMessage = error?.graphQLErrors?.[0]?.message;
    const networkGraphQlMessage = error?.networkError?.result?.errors?.[0]?.message;
    const topLevelMessage = error?.message;
    const rawMessage = graphQlMessage || networkGraphQlMessage || topLevelMessage || '';
    return sanitizeClientFacingLinkToolError(rawMessage, fallbackMessage);
};

const getShouldHideUrlTranscriptIntro = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return window.localStorage.getItem(URL_TRANSCRIPT_INTRO_DISMISS_KEY) === 'true';
    } catch {
        return false;
    }
};

const isStaleTranscriptProcessing = (audioProcessing) => {
    if (!isAudioProcessingActive(audioProcessing)) {
        return false;
    }

    const retryAtMs = audioProcessing?.nextRetryAt
        ? new Date(audioProcessing.nextRetryAt).getTime()
        : 0;
    // A durable queue deliberately leaves a throttled job pending until its
    // provider cooldown expires. It is not stale just because it is older than
    // the normal request timeout.
    if (String(audioProcessing?.status || '').toLowerCase() === 'pending' &&
        Number.isFinite(retryAtMs) && retryAtMs > Date.now()) {
        return false;
    }

    const startedAtMs = audioProcessing?.startedAt
        ? new Date(audioProcessing.startedAt).getTime()
        : 0;

    // Older/incomplete processing records can be left without a start time.
    // They cannot represent an active request reliably, so allow the user to retry.
    return !Number.isFinite(startedAtMs) || startedAtMs <= 0 ||
        Date.now() - startedAtMs > URL_TRANSCRIPT_STALE_PROCESSING_MS;
};

const getQueuedTranscriptStatus = audioProcessing => {
    if (String(audioProcessing?.status || '').toLowerCase() !== 'pending') {
        return null;
    }

    const retryAtMs = audioProcessing?.nextRetryAt
        ? new Date(audioProcessing.nextRetryAt).getTime()
        : 0;
    if (Number.isFinite(retryAtMs) && retryAtMs > Date.now()) {
        const minutes = Math.max(1, Math.ceil((retryAtMs - Date.now()) / 60_000));
        return `Transcript queued. Retrying in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
    }

    return 'Transcript queued. We’ll add it when it’s ready.';
};

const applyCurrentMessagePatch = (message, patch = {}) => {
    if (!message?.id || !patch || typeof patch !== 'object') {
        return null;
    }

    const updatedMessage = {
        ...message,
        ...patch,
        updatedAt: patch.updatedAt || new Date().toISOString()
    };

    try {
        Object.assign(message, updatedMessage);
    } catch (error) {
        console.error('Failed to apply local message patch', error);
    }

    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent('messageUpdated', {
                detail: {
                    message: updatedMessage
                }
            })
        );
    }

    return updatedMessage;
};

function UrlTranscriptButton({
    message,
    metaData,
    urlMetaIndex = 0,
    variant = 'light',
    size = 'default',
    fullWidth = false,
    presentation = 'button',
    onShowTranscript,
    children,
    hideWhileProcessing = false,
    fileVideo = false
}) {
    const {t: lnT} = useTranslation();

    const [status, setStatus] = React.useState(null);
    const [isStatusError, setIsStatusError] = React.useState(false);
    const [showTranscriptionIntroModal, setShowTranscriptionIntroModal] = React.useState(false);
    const [hideTranscriptionIntro, setHideTranscriptionIntro] = React.useState(false);
    const primaryUrl = React.useMemo(
        () => resolvePrimaryUrl({ metaData, message, urlMetaIndex }),
        [lnT, metaData, message, urlMetaIndex]
    );
    const youtubeUrl = React.useMemo(
        () => resolveYouTubeUrl(primaryUrl),
        [lnT, primaryUrl]
    );
    const socialVideoUrl = React.useMemo(
        () => resolveSocialVideoUrl(primaryUrl, metaData),
        [lnT, primaryUrl, metaData]
    );
    const xVideoUrl = React.useMemo(
        () => resolveXVideoUrl(primaryUrl, metaData),
        [lnT, primaryUrl, metaData]
    );
    const transcriptFeatureFlagName = fileVideo
        ? 'audio_transcription'
        : socialVideoUrl || xVideoUrl
        ? VIDEO_TRANSCRIPTION_ACCESS_FLAG
        : URL_TRANSCRIPT_FLAG;
    const hasTranscript = Boolean(
        message?.transcript?.text &&
        (
            message?.transcript?.urlMetaIndex == null ||
            Number(message.transcript.urlMetaIndex) === Number(urlMetaIndex)
        )
    );
    const transcriptProcessing = message?.metadata?.audioProcessing;
    const isTranscriptProcessingStale = isStaleTranscriptProcessing(transcriptProcessing);
    const isTranscriptProcessing = isAudioProcessingActive(transcriptProcessing) && !isTranscriptProcessingStale;
    const transcriptProcessingStatus = getAudioProcessingStatusText(transcriptProcessing);
    const queuedTranscriptStatus = getQueuedTranscriptStatus(transcriptProcessing);

    const { data: userData } = useQuery(QUERY.ME, {
        errorPolicy: 'all'
    });
    const { data: transcriptionFlagData, loading: transcriptionFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: transcriptFeatureFlagName },
        errorPolicy: 'all'
    });
    const transcriptionFlag = transcriptionFlagData?.getFeatureFlag;
    const transcriptionAccessDecision = React.useMemo(
        () => evaluateFeatureFlagForUser(transcriptionFlag, userData?.me, { loading: transcriptionFlagLoading }),
        [lnT, transcriptionFlag, userData?.me, transcriptionFlagLoading]
    );
    const showTranscriptAction = Boolean(
        (hasTranscript && typeof onShowTranscript === 'function') ||
        ((fileVideo || youtubeUrl || socialVideoUrl || xVideoUrl) && transcriptionFlag?.enabled)
    );

    const [extractUrlTranscriptMutation, { loading: isExtracting }] = useMutation(EXTRACT_URL_TRANSCRIPT);
    const [transcribeAudioMutation, { loading: isTranscribingFile }] = useMutation(TRANSCRIBE_AUDIO);

    React.useEffect(() => {
        setStatus(null);
        setIsStatusError(false);
    }, [message?.id, primaryUrl, urlMetaIndex]);

    const startTranscribe = React.useCallback(async () => {
        if (!showTranscriptAction || !message?.id || isTranscriptProcessing) {
            return;
        }

        if (transcriptionAccessDecision?.requiresUpgrade) {
            setIsStatusError(false);
            setStatus(lnT('messages:openingUpgradeOptions_e109ae86'));
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                upgradeType: 'personal',
                allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
            });
            return;
        }

        if (transcriptionAccessDecision?.requiresTeamUpgrade) {
            setIsStatusError(false);
            setStatus(lnT('messages:openingTeamUpgradeOptions_c575e41f'));
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                upgradeType: 'team',
                allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
            });
            return;
        }

        if (!transcriptionAccessDecision?.allowed) {
            const fallbackMessage = (() => {
                switch (transcriptionAccessDecision?.reason) {
                case 'LOADING':
                    return 'Checking transcript availability. Please try again.';
                case 'FLAG_DISABLED':
                    return 'Transcript extraction is currently disabled';
                case 'TEAM_CONTEXT_REQUIRED':
                    return 'Transcript extraction requires an active team workspace context.';
                default:
                    return 'Transcript extraction is unavailable right now.';
                }
            })();
            setIsStatusError(true);
            setStatus(fallbackMessage);
            return;
        }

        setIsStatusError(false);
        setStatus(lnT('messages:transcribing_4e9a9366'));

        try {
            if (fileVideo) {
                const response = await transcribeAudioMutation({
                    variables: {
                        data: {
                            messageId: message.id,
                            outputFormat: 'TIMESTAMPED',
                            provider: 'openai'
                        }
                    }
                });
                const result = response?.data?.transcribeAudio;
                if (result?.__typename === 'TranscribeAudioError') {
                    setIsStatusError(true);
                    setStatus(result.message || lnT('common:transcriptExtractionFailed'));
                    return;
                }
                if (result?.id) {
                    applyCurrentMessagePatch(message, result);
                }
                setIsStatusError(false);
                setStatus(result?.transcript ? lnT('messages:transcriptAddedToNote') : lnT('common:transcriptionIsRunningInTheBackground'));
                return;
            }

            const response = await extractUrlTranscriptMutation({
                variables: {
                    data: {
                        messageId: message.id,
                        urlMetaIndex,
                        format: 'PLAIN',
                        allowProviderFallback: true
                    }
                }
            });

            const result = response?.data?.extractUrlTranscript;
            if (result?.__typename === 'ExtractUrlTranscriptError') {
                const uiAction = mapTranscriptionErrorToUiAction({
                    error: result,
                    defaultDecision: transcriptionAccessDecision
                });
                if (uiAction.openUpgradeModal) {
                    setIsStatusError(false);
                    setStatus(lnT('messages:openingUpgradeOptions_e109ae86'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                        upgradeType: 'personal',
                        allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
                    });
                    return;
                }
                if (uiAction.openTeamUpgradeModal) {
                    setIsStatusError(false);
                    setStatus(lnT('messages:openingTeamUpgradeOptions_c575e41f'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.URL_TRANSCRIPT,
                        upgradeType: 'team',
                        allowedPlanTypes: transcriptionFlag?.allowedPlanTypes
                    });
                    return;
                }
                const errorMessage = sanitizeClientFacingLinkToolError(
                    uiAction.showMessage || result?.message,
                    'Transcript extraction failed'
                );
                applyCurrentMessagePatch(message, {
                    metadata: {
                        ...(message?.metadata || {}),
                        audioProcessing: {
                            ...(message?.metadata?.audioProcessing || {}),
                            status: 'failed',
                            outputFormat: 'PLAIN',
                            error: errorMessage
                        }
                    }
                });
                setIsStatusError(true);
                setStatus(errorMessage);
                return;
            }

            if (result?.id) {
                applyCurrentMessagePatch(message, {
                    ...(typeof result?.body === 'string' ? { body: result.body } : {}),
                    ...(result?.transcript ? { transcript: result.transcript } : {}),
                    ...(Array.isArray(result?.transcriptionSegments)
                        ? { transcriptionSegments: result.transcriptionSegments }
                        : {}),
                    ...(result?.metadata ? { metadata: result.metadata } : {})
                });
            }

            customAnalytics.message_action({
                action: 'Extract URL Transcript',
                type: 'Link'
            }, message);
            setIsStatusError(false);
            setStatus(
                ['pending', 'processing'].includes(String(result?.metadata?.audioProcessing?.status || '').toLowerCase())
                    ? lnT('common:transcriptQueuedWeLlAddItWhen')
                    : lnT('messages:transcriptAddedToNote')
            );
        } catch (error) {
            const errorMessage = resolveActionErrorMessage(error, 'Transcript extraction failed');
            applyCurrentMessagePatch(message, {
                metadata: {
                    ...(message?.metadata || {}),
                    audioProcessing: {
                        ...(message?.metadata?.audioProcessing || {}),
                        status: 'failed',
                        outputFormat: 'PLAIN',
                        error: errorMessage
                    }
                }
            });
            setIsStatusError(true);
            setStatus(errorMessage);
        }
    }, [lnT, showTranscriptAction, message, isTranscriptProcessing, transcriptionAccessDecision, extractUrlTranscriptMutation, fileVideo, transcribeAudioMutation, urlMetaIndex]);

    const handleTranscribe = React.useCallback((event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (!showTranscriptAction || !message?.id) {
            return;
        }

        if (isTranscriptProcessing) {
            setIsStatusError(false);
            setStatus(lnT('messages:working'));
            return;
        }

        if (hasTranscript) {
            onShowTranscript?.();
            return;
        }

        if (
            transcriptionAccessDecision?.requiresUpgrade ||
            transcriptionAccessDecision?.requiresTeamUpgrade ||
            !transcriptionAccessDecision?.allowed
        ) {
            startTranscribe();
            return;
        }

        if (!getShouldHideUrlTranscriptIntro()) {
            setHideTranscriptionIntro(false);
            setShowTranscriptionIntroModal(true);
            return;
        }

        startTranscribe();
    }, [lnT, hasTranscript, onShowTranscript, showTranscriptAction, message?.id, isTranscriptProcessing, transcriptionAccessDecision, startTranscribe]);

    const handleCloseTranscriptionIntroModal = React.useCallback(() => {
        setShowTranscriptionIntroModal(false);
        setHideTranscriptionIntro(false);
    }, [lnT]);

    const handleConfirmTranscriptionIntro = React.useCallback(() => {
        if (hideTranscriptionIntro && typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(URL_TRANSCRIPT_INTRO_DISMISS_KEY, 'true');
            } catch {
                // Ignore storage limitations
            }
        }

        setShowTranscriptionIntroModal(false);
        setHideTranscriptionIntro(false);
        startTranscribe();
    }, [lnT, hideTranscriptionIntro, startTranscribe]);

    if (!showTranscriptAction || !message?.id) {
        return null;
    }

    if (hideWhileProcessing && isTranscriptProcessing && !hasTranscript) {
        return null;
    }

    const isWorking = isExtracting || isTranscribingFile;
    const buttonLabel = hasTranscript
        ? lnT('common:showTranscript')
        : isTranscriptProcessingStale
            ? lnT('common:tryAgain')
            : ((isWorking || isTranscriptProcessing) ? lnT('messages:working') : lnT('messages:getTranscript'));
    const visibleStatus = isTranscriptProcessing && !queuedTranscriptStatus
        ? null
        : (isTranscriptProcessingStale
            ? lnT('common:previousTranscriptionDidNotFinishYouCan')
            : (queuedTranscriptStatus || transcriptProcessingStatus || status));

    return (
        <TranscriptButtonStack $fullWidth={fullWidth}>
            <TranscriptButton
                type="button"
                onClick={handleTranscribe}
                disabled={isWorking || isTranscriptProcessing}
                $variant={variant}
                $size={size}
                $fullWidth={fullWidth}
                $presentation={presentation}
            >
                {children || buttonLabel}
            </TranscriptButton>
            {visibleStatus && !isWorking ? (
                <TranscriptStatus $variant={variant} $isError={isStatusError}>
                    {visibleStatus}
                </TranscriptStatus>
            ) : null}
            <Modal
                isOpen={showTranscriptionIntroModal}
                onRequestClose={handleCloseTranscriptionIntroModal}
                style={{
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                        zIndex: 10002
                    },
                    content: {
                        maxWidth: 420,
                        width: 'calc(100vw - 32px)',
                        inset: '50% auto auto 50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: 16,
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: '#12161d',
                        padding: 0,
                        overflow: 'hidden',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.45)'
                    }
                }}
                contentLabel={lnT('common:linkTranscriptNotice')}
            >
                <div style={{ padding: 20 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'rgba(255, 255, 255, 0.94)', marginBottom: 8 }}>{lnT('common:getATranscriptForThis')}{fileVideo ? lnT('common:video') : lnT('common:link')}?
                    </div>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}
                    >{lnT('common:weLlExtractTheTranscriptAndAdd')}</p>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 16,
                            fontSize: 14,
                            color: 'rgba(255, 255, 255, 0.84)',
                            cursor: 'pointer'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={hideTranscriptionIntro}
                            onChange={e => setHideTranscriptionIntro(e.target.checked)}
                        />{lnT('messages:donTShowThisAgain')}</label>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 10,
                            marginTop: 20
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleCloseTranscriptionIntroModal}
                            style={{
                                border: '1px solid rgba(255, 255, 255, 0.14)',
                                background: 'rgba(255, 255, 255, 0.04)',
                                color: 'rgba(255, 255, 255, 0.84)',
                                borderRadius: 10,
                                padding: '10px 14px',
                                fontSize: 14,
                                cursor: 'pointer'
                            }}
                        >{lnT('common:cancel')}</button>
                        <button
                            type="button"
                            onClick={handleConfirmTranscriptionIntro}
                            style={{
                                border: '1px solid #598bff',
                                background: '#598bff',
                                color: '#fff',
                                borderRadius: 10,
                                padding: '10px 14px',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >{lnT('common:continue')}</button>
                    </div>
                </div>
            </Modal>
        </TranscriptButtonStack>
    );
}

export const TranscriptButtonStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${props => (props.$fullWidth ? 'stretch' : 'flex-start')};
    gap: 6px;
    min-width: 0;
    flex-shrink: 0;
    width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

export const TranscriptButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: ${props => (props.$presentation === 'menu-item' ? 'flex-start' : 'center')};
    min-height: ${props => (
        props.$presentation === 'menu-item'
            ? 'auto'
            : props.$presentation === 'text'
            ? '0'
            : props.$size === 'compact'
                ? '32px'
                : '36px'
    )};
    padding: ${props => {
        if (props.$presentation === 'text') {
            return '0';
        }
        if (props.$presentation === 'menu-item') {
            return '10px 12px';
        }
        return props.$size === 'compact' ? '6px 12px' : '8px 14px';
    }};
    width: ${props => (props.$fullWidth || props.$presentation === 'menu-item' ? '100%' : 'auto')};
    border-radius: ${props => (props.$presentation === 'text' ? '0' : props.$presentation === 'menu-item' ? '10px' : '8px')};
    border: ${props => (
        props.$presentation === 'text' || props.$presentation === 'menu-item'
            ? 'none'
            : `1px solid ${props.$variant === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(37, 99, 235, 0.18)'}`
    )};
    background: ${props => (
        props.$presentation === 'text' || props.$presentation === 'menu-item'
            ? 'transparent'
            : props.$variant === 'dark'
                ? 'rgba(255, 255, 255, 0.06)'
                : PRIMARY_COLOR_1
    )};
    color: ${props => (
        props.$presentation === 'menu-item'
            ? 'rgba(255, 255, 255, 0.9)'
            :
        props.$presentation === 'text'
            ? (props.$variant === 'dark' ? '#2b88d9' : PRIMARY_COLOR_4)
            : props.$variant === 'dark'
                ? 'rgba(255, 255, 255, 0.92)'
                : PRIMARY_COLOR
    )};
    font-size: ${props => (props.$presentation === 'text' ? '12px' : '13px')};
    font-weight: ${props => (props.$presentation === 'menu-item' ? 500 : 600)};
    line-height: 1.2;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s ease-in-out, border-color 0.15s ease-in-out, opacity 0.15s ease-in-out, color 0.15s ease-in-out;

    &:hover:not(:disabled) {
        background: ${props => {
            if (props.$presentation === 'menu-item') {
                return 'rgba(255, 255, 255, 0.08)';
            }
            if (props.$presentation === 'text') {
                return 'transparent';
            }
            return props.$variant === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(37, 99, 235, 0.12)';
        }};
        border-color: ${props => {
            if (props.$presentation === 'text') {
                return 'transparent';
            }
            if (props.$presentation === 'menu-item') {
                return 'transparent';
            }
            return props.$variant === 'dark'
                ? 'rgba(255, 255, 255, 0.24)'
                : 'rgba(37, 99, 235, 0.28)';
        }};
        color: ${props => (
            props.$presentation === 'menu-item'
                ? 'rgba(255, 255, 255, 0.9)'
                :
            props.$presentation === 'text'
                ? (props.$variant === 'dark' ? '#2b88d9' : PRIMARY_COLOR_4)
                : props.$variant === 'dark'
                    ? 'rgba(255, 255, 255, 0.92)'
                    : PRIMARY_COLOR
        )};
        text-decoration: ${props => (props.$presentation === 'text' ? 'underline' : 'none')};
    }

    &:disabled {
        cursor: wait;
        opacity: 0.7;
    }
`;

export const TranscriptStatus = styled.div`
    max-width: 100%;
    color: ${props => {
        if (props.$isError) {
            return props.$variant === 'dark' ? '#fca5a5' : '#b91c1c';
        }
        return props.$variant === 'dark' ? 'rgba(255, 255, 255, 0.78)' : PRIMARY_COLOR_4;
    }};
    font-size: 12px;
    line-height: 1.4;
    opacity: ${props => (props.$isError ? 1 : 0.9)};
    word-break: break-word;
`;

export default UrlTranscriptButton;
